import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import { z } from 'zod';

import { codec, useQueryParams } from '@/hooks/router/use-query-params';

import type { Filters } from './selected-filters';
import { initialFilters } from './selected-filters';

// URL schema (human-readable)
const UrlFieldsSchema = z.object({
  // core
  from: z.date().optional(),
  to: z.date().optional(),
  domain: z
    .union([
      z.literal('battery'),
      z.literal('pcs'),
      z.literal('hvac'),
      z.literal('other'),
    ])
    .optional(),
  siteId: z.string().optional(),

  // battery level filters (arrays of string IDs)
  system: z.array(z.string()).optional(),
  array: z.array(z.string()).optional(),
  node: z.array(z.string()).optional(),
  container: z.array(z.string()).optional(),
  bank: z.array(z.string()).optional(),
  pack: z.array(z.string()).optional(),
  module: z.array(z.string()).optional(),

  // pcs level filters
  pcs: z.array(z.string()).optional(),
});

type UrlFields = z.infer<typeof UrlFieldsSchema>;

export function useSystemLogsQueryState(): [
  Filters,
  Dispatch<SetStateAction<Filters>>,
] {
  const [fields, update] = useQueryParams<UrlFields>({
    from: { key: 'from', schema: z.date().optional(), codec: codec.date },
    to: { key: 'to', schema: z.date().optional(), codec: codec.date },
    domain: {
      key: 'domain',
      schema: z
        .union([
          z.literal('battery'),
          z.literal('pcs'),
          z.literal('hvac'),
          z.literal('other'),
        ])
        .optional(),
      codec: codec.string,
    },
    siteId: {
      key: 'site_id',
      schema: z.string().optional(),
      codec: codec.string,
    },

    system: {
      key: 'system',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
    array: {
      key: 'array',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
    node: {
      key: 'node',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
    container: {
      key: 'container',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
    bank: {
      key: 'bank',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
    pack: {
      key: 'pack',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
    module: {
      key: 'module',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },

    pcs: {
      key: 'pcs',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string),
    },
  });

  // Build Filters from URL fields (fallback to initialFilters)
  // ✅ memoize filters so it's stable between renders
  const filters: Filters = useMemo(
    () => ({
      coreFilters: {
        dateRange: { from: fields.from, to: fields.to },
        domain: fields.domain,
        siteId: fields.siteId,
      },
      batteryFilters: {
        ...(fields.system ? { systemId: fields.system } : {}),
        ...(fields.array ? { arrayId: fields.array } : {}),
        ...(fields.node ? { nodeId: fields.node } : {}),
        ...(fields.container ? { containerId: fields.container } : {}),
        ...(fields.bank ? { bankId: fields.bank } : {}),
        ...(fields.pack ? { packId: fields.pack } : {}),
        ...(fields.module ? { moduleId: fields.module } : {}),
      } as Filters['batteryFilters'],
      pcsFilters: {
        ...(fields.pcs ? { pcsId: fields.pcs } : {}),
      } as Filters['pcsFilters'],
    }),
    [fields],
  );

  const setFilters: Dispatch<SetStateAction<Filters>> = useCallback(
    (updator) => {
      const next =
        typeof updator === 'function'
          ? (updator as (s: Filters) => Filters)(filters)
          : updator;

      update.replace(() => ({
        from: next.coreFilters.dateRange?.from,
        to: next.coreFilters.dateRange?.to,
        domain: next.coreFilters.domain ?? undefined,
        siteId: next.coreFilters.siteId ?? undefined,

        system: Array.isArray((next.batteryFilters as any).systemId)
          ? (next.batteryFilters as any).systemId
          : undefined,
        array: Array.isArray((next.batteryFilters as any).arrayId)
          ? (next.batteryFilters as any).arrayId
          : undefined,
        node: Array.isArray((next.batteryFilters as any).nodeId)
          ? (next.batteryFilters as any).nodeId
          : undefined,
        container: Array.isArray((next.batteryFilters as any).containerId)
          ? (next.batteryFilters as any).containerId
          : undefined,
        bank: Array.isArray((next.batteryFilters as any).bankId)
          ? (next.batteryFilters as any).bankId
          : undefined,
        pack: Array.isArray((next.batteryFilters as any).packId)
          ? (next.batteryFilters as any).packId
          : undefined,
        module: Array.isArray((next.batteryFilters as any).moduleId)
          ? (next.batteryFilters as any).moduleId
          : undefined,

        pcs: Array.isArray((next.pcsFilters as any).pcsId)
          ? (next.pcsFilters as any).pcsId
          : undefined,
      }));

      return next;
    },
    [filters, update],
  );

  const effective = UrlFieldsSchema.safeParse(fields).success
    ? filters
    : initialFilters;

  return [effective, setFilters];
}
